(() => {
  const SELECTORS = {
    // common Shopify / theme deferred media triggers
    deferredButtons: [
      "[data-deferred-media-button]",
      "deferred-media button",
      "deferred-media [type='button']",
      ".deferred-media__poster-button",
      ".media__poster button",
      ".product__media button[aria-label*='Play']",
    ].join(","),
    // videos we want to autoplay
    videos: "video",
  };

  const playedOnce = new WeakSet();

  function safePlay(video) {
    if (!video) return;

    // Autoplay requirements
    video.muted = true;
    video.setAttribute("muted", "");
    video.setAttribute("playsinline", "");
    video.playsInline = true;
    video.loop = true;

    // Some themes set autoplay=false on the element
    video.autoplay = true;
    video.setAttribute("autoplay", "");

    // Try play
    const p = video.play();
    if (p && typeof p.catch === "function") p.catch(() => {});
  }

  function activateDeferredMedia(root = document) {
    root.querySelectorAll(SELECTORS.deferredButtons).forEach((btn) => {
      // Avoid repeated clicking
      if (btn.dataset.autoplayActivated === "1") return;

      // Only activate buttons that live inside product tiles/media areas
      const inProductContext = btn.closest(
        ".product-grid-item, .product-card, .card, .product__media, .product__media__container, deferred-media"
      );
      if (!inProductContext) return;

      btn.dataset.autoplayActivated = "1";
      try {
        btn.click();
      } catch (_) {}
    });
  }

  function scanAndPlay(root = document) {
    // First activate posters -> real media (creates <video>)
    activateDeferredMedia(root);

    // Then play any videos we can see
    root.querySelectorAll(SELECTORS.videos).forEach((video) => {
      // Only attempt play if it’s within a product tile/media region
      const inProductContext = video.closest(
        ".product-grid-item, .product-card, .card, .product__media, .product__media__container, deferred-media"
      );
      if (!inProductContext) return;

      // If using IntersectionObserver, we will handle play/pause there.
      // But also do one immediate attempt for already-visible videos.
      safePlay(video);
    });
  }

  function setupIntersectionAutoplay() {
    if (!("IntersectionObserver" in window)) {
      // Fallback: just keep scanning
      scanAndPlay(document);
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (!(video instanceof HTMLVideoElement)) return;

          if (entry.isIntersecting) {
            safePlay(video);
            playedOnce.add(video);
          } else {
            // Optional: pause when out of view to reduce CPU
            // (comment out if you truly want everything playing constantly)
            try { video.pause(); } catch (_) {}
          }
        });
      },
      { threshold: 0.25 }
    );

    // Observe videos as they appear
    const observeAll = (root = document) => {
      root.querySelectorAll("video").forEach((v) => {
        if (v.dataset.autoplayObserved === "1") return;
        const inProductContext = v.closest(
          ".product-grid-item, .product-card, .card, .product__media, .product__media__container, deferred-media"
        );
        if (!inProductContext) return;

        v.dataset.autoplayObserved = "1";
        io.observe(v);
      });
    };

    // Initial
    scanAndPlay(document);
    observeAll(document);

    // Watch for new tiles (carousels/lazy sections)
    const mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;
          // Activate any new deferred posters
          activateDeferredMedia(node);
          // Observe any new videos created
          observeAll(node);
          // Attempt play
          scanAndPlay(node);
        });
      }
    });

    mo.observe(document.documentElement, { childList: true, subtree: true });
  }

  document.addEventListener("DOMContentLoaded", setupIntersectionAutoplay);
})();